import React from 'react';
import { Conversation } from '../../utils/mockData'; // Adjust path as needed
import {
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  useTheme,
} from '@mui/material';
import { formatDistanceToNowStrict, format } from 'date-fns'; // For formatting dates

interface ConversationListItemProps {
  conversation: Conversation;
  isSelected: boolean;
  onSelect: (conversationId: string) => void;
}

const ConversationListItem: React.FC<ConversationListItemProps> = ({
  conversation,
  isSelected,
  onSelect,
}) => {
  const theme = useTheme();
  const lastMessage = conversation.messages[conversation.messages.length - 1];

  const formatLastUpdated = (date: Date): string => {
    // More sophisticated date formatting:
    // If today: "10:30 AM"
    // If yesterday: "Yesterday"
    // If within last week: "Mon", "Tue", etc.
    // Else: "MM/dd/yyyy"
    const now = new Date();
    if (format(date, 'yyyyMMdd') === format(now, 'yyyyMMdd')) {
      return format(date, 'p'); // e.g., 10:30 AM
    }
    if (format(new Date(now.setDate(now.getDate() -1)), 'yyyyMMdd') === format(date, 'yyyyMMdd')) {
        return 'Yesterday';
    }
    if (date > new Date(now.setDate(now.getDate() - 7))) {
        return format(date, 'E'); // e.g., Mon
    }
    return format(date, 'MM/dd/yyyy');
  };

  const lastMessageSnippet = lastMessage
    ? `${lastMessage.sender === 'user' ? 'You: ' : ''}${lastMessage.text}`
    : 'No messages yet...';

  return (
    <ListItemButton
      selected={isSelected}
      onClick={() => onSelect(conversation.id)}
      sx={{
        borderRadius: '8px', // Add some rounding to the items
        mb: 0.5, // Add a little margin between items
        '&.Mui-selected': {
          backgroundColor: theme.palette.primary.main, // More prominent selection
          color: theme.palette.primary.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
          '& .MuiListItemText-secondary': { // Ensure secondary text is also contrast color
            color: theme.palette.primary.contrastText,
            opacity: 0.8,
          },
          '& .MuiTypography-caption': { // Ensure timestamp is also contrast color
            color: theme.palette.primary.contrastText,
            opacity: 0.7,
          }
        },
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        py: 1.25,
        px: 1.5,
        alignItems: 'flex-start',
      }}
    >
      <ListItemText
        primary={
          <Typography
            variant="subtitle2" // Slightly smaller for a denser list
            component="div"
            noWrap
            sx={{
                fontWeight: isSelected ? 600 : 500,
                color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.primary,
                mb: 0.25
            }}
          >
            {conversation.title}
          </Typography>
        }
        secondary={
          <Typography
            variant="body2" // Keep body2 for snippet
            // color is handled by .Mui-selected secondary selector or defaults to text.secondary
            noWrap
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 1, // Show only 1 line for snippet for denser look
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.3,
              color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.secondary,
              opacity: isSelected ? 0.8 : 1,
            }}
          >
            {lastMessageSnippet}
          </Typography>
        }
        sx={{ my: 0, pr: 1 }} // Add some padding to the right of text, before timestamp
      />
      <Typography
        variant="caption"
        sx={{
            ml: 1,
            whiteSpace: 'nowrap',
            pt: 0.5, // Align with primary text's top
            color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.disabled, // More subtle timestamp
            opacity: isSelected ? 0.7 : 1,
        }}
      >
        {formatLastUpdated(conversation.lastUpdated)}
      </Typography>
    </ListItemButton>
  );
};

export default ConversationListItem;
